import { useState, useEffect } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../../popover";
import { Button } from "../../button";
import { Input } from "../../input";
import MagnifyingGlass from "../../../assets/icons/magnifying-glass-small-gray.svg";
import TableFilterState from "./table-filter-state";

type StablePopoverTextFilterProps = {
  filterState: TableFilterState<any>;
  placeholder?: string | null;
  title?: string | null;
  description?: string | null;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const StablePopoverTextFilter = ({
  filterState,
  placeholder,
  title,
  description,
  isOpen = false,
  onOpenChange
}: StablePopoverTextFilterProps) => {
  // Check if filter has value
  const hasValue = filterState.filter[filterState.filterKey] && filterState.filter[filterState.filterKey].trim() !== '';
  
  // Local state for the input value
  const [tempValue, setTempValue] = useState(
    filterState.filter[filterState.filterKey] || ""
  );

  // Update tempValue when filter changes from outside (e.g., when clearing filters)
  useEffect(() => {
    setTempValue(filterState.filter[filterState.filterKey] || "");
  }, [filterState.filter[filterState.filterKey]]);

  const handleOpenChange = (newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    }
  };

  const handleApply = () => {
    filterState.setFilter((prev: any) => ({
      ...prev,
      [filterState.filterKey]: tempValue.trim(),
    }));
    handleOpenChange(false);
  };

  const handleClear = () => {
    setTempValue("");
    filterState.setFilter((prev: any) => ({
      ...prev,
      [filterState.filterKey]: "",
    }));
    handleOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleApply();
    }
    // Prevent event bubbling to avoid closing popover
    e.stopPropagation();
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <img 
          className={`ml-2 w-3 cursor-pointer ${hasValue ? 'opacity-100' : 'opacity-60'}`} 
          src={MagnifyingGlass} 
          alt="Search" 
        />
      </PopoverTrigger>
      <PopoverContent className="w-64 p-4" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="space-y-4">
          {title && (
            <div className="text-xs text-[#6A7282] font-medium">
              {title}
            </div>
          )}

          <Input
            placeholder={placeholder || "Search..."}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full"
            autoFocus
          />

          {description && (
            <div className="text-xs text-gray-500">
              {description}
            </div>
          )}

          <div className="flex gap-2">
            <Button
              className="flex-1 text-xs"
              size="sm"
              onClick={handleApply}
            >
              Apply
            </Button>
            <Button
              className="flex-1 text-xs"
              variant="outline"
              size="sm"
              onClick={handleClear}
            >
              Clear
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default StablePopoverTextFilter;
